import { defineStore } from 'pinia'
import { authService } from '../services/auth'
import { apiRequest, API_ENDPOINTS } from '../services/api'
import { formatLocalDate } from '../services/utils'
import type { UserInfo } from '../types/auth'

export type UserRole = 'user' | 'admin' | 'superAdmin'

export const useUserStore = defineStore('user', {
  state: () => {
    return {
      userName: '',
      email: '',
      phone: '',
      memberSince: '',
      pfp: '',
      is2FAEnabled: false,
      userType: 'personal',
      role: 'user' as UserRole,
      // 新增字段
      userInfo: null as UserInfo | null,
      isAuthenticated: false,
      isRefreshing: false, // 添加刷新状态
    }
  },

  getters: {
    // 根据后端角色信息判断用户角色
    currentRole(): UserRole {
      if (!this.userInfo || !this.userInfo.roles || this.userInfo.roles.length === 0) {
        return 'user'
      }
      
      // 根据角色ID判断权限级别，优先级：1(超级管理员) > 3(管理员) > 2(普通用户)
      // 注意：后端返回的是数字类型，需要转换为字符串或数字进行比较
      const roleIds = this.userInfo.roles.map(role => String(role.id))
      
      console.log('用户角色信息:', this.userInfo.roles)
      console.log('角色ID列表:', roleIds)
      
      // 先判断是否包含ID为1的角色（超级管理员）
      if (roleIds.includes('1')) {
        console.log('用户是超级管理员')
        return 'superAdmin'
      }
      
      // 再判断是否包含ID为3的角色（管理员）
      if (roleIds.includes('3')) {
        console.log('用户是管理员')
        return 'admin'
      }
      
      // 最后判断是否包含ID为2的角色（普通用户）
      if (roleIds.includes('2')) {
        console.log('用户是普通用户')
        return 'user'
      }
      
      console.log('用户没有匹配的角色，默认为普通用户')
      // 如果没有匹配的角色ID，返回默认用户类型
      return 'user'
    },

    // 获取用户权限列表
    permissions(): string[] {
      if (!this.userInfo || !this.userInfo.roles) {
        return []
      }
      
      const permissions: string[] = []
      this.userInfo.roles.forEach(role => {
        if (role.permissions) {
          role.permissions.forEach(permission => {
            permissions.push(permission.uri)
          })
        }
      })
      
      return permissions
    }
  },

  actions: {
    toggle2FA() {
      this.is2FAEnabled = !this.is2FAEnabled
    },

    changeUserName(userName: string) {
      this.userName = userName
    },

    setRole(role: UserRole) {
      this.role = role
    },

    // 设置用户信息
    setUserInfo(userInfo: UserInfo) {
      console.log('设置用户信息:', userInfo)
      console.log('用户邮箱:', userInfo.email)
      console.log('用户手机号:', userInfo.phone)
      
      this.userInfo = userInfo
      this.userName = userInfo.username
      this.email = userInfo.email || ''
      this.phone = userInfo.phone || ''
      
      console.log('Store中设置的邮箱:', this.email)
      console.log('Store中设置的手机号:', this.phone)
      
      // 根据后端返回的type字段判断用户类型，1为个人用户，2为企业用户
      this.userType = userInfo.type === 1 ? 'personal' : 'business'
      // 将UTC时间转换为本地日期格式（只保留日期）
      this.memberSince = formatLocalDate(userInfo.createTime)
      this.isAuthenticated = true
      
      // 根据后端角色信息设置前端角色
      this.role = this.currentRole
    },

    // 清除用户信息
    clearUserInfo() {
      this.userInfo = null
      this.isAuthenticated = false
      this.role = 'user'
    },

    // 检查是否有特定权限
    hasPermission(uri: string): boolean {
      return this.permissions.includes(uri)
    },

    // 检查是否有特定角色
    hasRole(roleId: string): boolean {
      if (!this.userInfo || !this.userInfo.roles) {
        return false
      }
      
      return this.userInfo.roles.some(role => String(role.id) === roleId)
    },

    // 登出
    logout() {
      this.clearUserInfo()
      authService.logout()
    },

    // 初始化用户状态（从本地存储恢复）
    initUserState() {
      const userInfo = authService.getUserInfo()
      if (userInfo) {
        this.setUserInfo(userInfo)
      }
    },

    // 强制刷新用户信息（清除缓存）
    async refreshUserInfo() {
      try {
        this.isRefreshing = true
        console.log('强制刷新用户信息，清除缓存...')
        
        // 重新获取用户信息（不清除本地存储，保持当前显示）
        const response = await apiRequest(API_ENDPOINTS.users.current(), {
          method: 'GET'
        })
        const userInfo = response.data
        
        console.log('刷新后的用户信息:', userInfo)
        
        // 更新store状态
        this.setUserInfo(userInfo)
        
        // 重新保存到本地存储
        authService.setUserInfo(userInfo)
        
        console.log('用户信息刷新完成')
        return userInfo
      } catch (error) {
        console.error('刷新用户信息失败:', error)
        throw error
      } finally {
        this.isRefreshing = false
      }
    },

    // 获取用户信息
    async fetchUserInfo() {
      try {
        const userInfo = await authService.getUserInfo()
        if (userInfo) {
          this.setUserInfo(userInfo)
        }
      } catch (error) {
        console.error('获取用户信息失败:', error)
        throw error
      }
    }
  },
})
